import * as XLSX from 'xlsx';

export interface LaborMaterialItem {
  description: string;
  uom: string;
  unitPrice: number;
  laborCost: number;
  materialCost: number;
  category: string;
  laborPercent: number;
  materialPercent: number;
}

export interface ExcelData {
  items: LaborMaterialItem[];
  categories: string[];
}

// Parse the Excel data and extract labor/material pricing
export const parseExcelData = (workbook: XLSX.WorkBook): ExcelData => {
  const items: LaborMaterialItem[] = [];
  const categories: string[] = [];
  
  // Get the first worksheet
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  
  // Convert to JSON with header row
  const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
  
  let currentCategory = '';
  let currentLaborPercent = 0;
  let currentMaterialPercent = 0;
  
  for (let i = 0; i < jsonData.length; i++) {
    const row = jsonData[i] as any[];
    
    // Skip empty rows
    if (!row || row.length === 0) continue;
    
    // Check for category headers (rows with text in first column)
    if (row[1] && typeof row[1] === 'string' && row[1].trim() && !row[1].includes('Description')) {
      const categoryText = row[1].trim();
      
      // Skip percentage rows
      if (categoryText.includes('%')) {
        // Extract percentages
        const percentMatch = categoryText.match(/(\d+)%,\s*(\d+)%/);
        if (percentMatch) {
          currentLaborPercent = parseInt(percentMatch[1]);
          currentMaterialPercent = parseInt(percentMatch[2]);
        }
        continue;
      }
      
      // Skip section headers
      if (categoryText.includes('ESTIMATING') || categoryText.includes('UNIT PRICING')) {
        continue;
      }
      
      // This is a category
      currentCategory = categoryText;
      if (!categories.includes(currentCategory)) {
        categories.push(currentCategory);
      }
      continue;
    }
    
    // Check for data rows (should have description, UOM, and unit price)
    if (row[1] && row[2] && row[3] && typeof row[1] === 'string' && typeof row[2] === 'string') {
      const description = row[1].trim();
      const uom = row[2].trim();
      const unitPriceStr = row[3];
      
      // Skip empty descriptions or headers
      if (!description || description === 'Description' || description === '') continue;
      
      // Parse unit price
      let unitPrice = 0;
      if (typeof unitPriceStr === 'string') {
        const priceMatch = unitPriceStr.match(/\$?([\d,]+\.?\d*)/);
        if (priceMatch) {
          unitPrice = parseFloat(priceMatch[1].replace(/,/g, ''));
        }
      } else if (typeof unitPriceStr === 'number') {
        unitPrice = unitPriceStr;
      }
      
      // Parse labor and material costs
      let laborCost = 0;
      let materialCost = 0;
      
      if (row[5] && typeof row[5] === 'string') {
        const laborMatch = row[5].match(/\$?([\d,]+\.?\d*)/);
        if (laborMatch) {
          laborCost = parseFloat(laborMatch[1].replace(/,/g, ''));
        }
      } else if (typeof row[5] === 'number') {
        laborCost = row[5];
      }
      
      if (row[6] && typeof row[6] === 'string') {
        const materialMatch = row[6].match(/\$?([\d,]+\.?\d*)/);
        if (materialMatch) {
          materialCost = parseFloat(materialMatch[1].replace(/,/g, ''));
        }
      } else if (typeof row[6] === 'number') {
        materialCost = row[6];
      }
      
      // If labor/material costs are 0, calculate from percentages
      if (laborCost === 0 && materialCost === 0 && currentLaborPercent > 0) {
        laborCost = (unitPrice * currentLaborPercent) / 100;
        materialCost = (unitPrice * currentMaterialPercent) / 100;
      }
      
      // Only add items with valid data
      if (unitPrice > 0 && description && uom) {
        items.push({
          description,
          uom,
          unitPrice,
          laborCost,
          materialCost,
          category: currentCategory,
          laborPercent: currentLaborPercent,
          materialPercent: currentMaterialPercent
        });
      }
    }
  }
  
  return { items, categories };
};

// Load Excel file from public folder
export const loadExcelData = async (): Promise<ExcelData> => {
  try {
    const response = await fetch('/Assets/Labor and material Unit Prices.xlsx');
    const arrayBuffer = await response.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    
    return parseExcelData(workbook);
  } catch (error) {
    console.error('Error loading Excel file:', error);
    // Return empty data if file can't be loaded
    return { items: [], categories: [] };
  }
};

// Convert Excel data to the format expected by the renovation estimator
export const convertToLaborUnitCosts = (excelData: ExcelData): Record<string, { unit: string; price: number; laborPercent: number; materialPercent: number }> => {
  const laborUnitCosts: Record<string, { unit: string; price: number; laborPercent: number; materialPercent: number }> = {};
  
  excelData.items.forEach(item => {
    // Clean up the description for use as a key
    const key = item.description.replace(/[^a-zA-Z0-9\s]/g, '').trim();
    
    laborUnitCosts[key] = {
      unit: item.uom,
      price: item.unitPrice,
      laborPercent: item.laborPercent || 0,
      materialPercent: item.materialPercent || 0
    };
  });
  
  return laborUnitCosts;
};
